import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { BonusAuditEntry, BonusAuditActionType } from '@/types/bonus';

interface BonusAuditContextType {
  entries: BonusAuditEntry[];
  addEntry: (agentId: string, agentName: string, actionType: BonusAuditActionType, amount: number, reason: string, performedBy: string, performedByName: string) => void;
  getEntriesForAgent: (agentId: string) => BonusAuditEntry[];
  getTotalAdjustmentsForAgent: (agentId: string) => number;
}

const BonusAuditContext = createContext<BonusAuditContextType | undefined>(undefined);

// Generate initial mock audit entries
const generateMockAuditEntries = (): BonusAuditEntry[] => {
  return [
    {
      id: 'audit1',
      agentId: 'u3',
      agentName: 'John Smith',
      actionType: 'ADD',
      amount: 50,
      reason: 'Excellent customer feedback on 3 consecutive deals',
      performedBy: 'u1',
      performedByName: 'Admin User',
      timestamp: new Date('2024-11-20T14:30:00'),
    },
    {
      id: 'audit2',
      agentId: 'u4',
      agentName: 'Emily Davis',
      actionType: 'DEDUCT',
      amount: 25,
      reason: 'Late submission of deal documentation',
      performedBy: 'u1',
      performedByName: 'Admin User',
      timestamp: new Date('2024-11-18T10:15:00'),
    },
    {
      id: 'audit3',
      agentId: 'u5',
      agentName: 'Michael Brown',
      actionType: 'ADD',
      amount: 100,
      reason: 'Top performer of the week bonus',
      performedBy: 'u1',
      performedByName: 'Admin User',
      timestamp: new Date('2024-11-25T16:00:00'),
    },
    {
      id: 'audit4',
      agentId: 'u3',
      agentName: 'John Smith',
      actionType: 'DEDUCT',
      amount: 30,
      reason: 'Client complaint - service not delivered on time',
      performedBy: 'u1',
      performedByName: 'Admin User',
      timestamp: new Date('2024-11-22T09:45:00'),
    },
    {
      id: 'audit5',
      agentId: 'u7',
      agentName: 'Ana Petrović',
      actionType: 'ADD',
      amount: 75,
      reason: 'Successfully upsold premium package',
      performedBy: 'u1',
      performedByName: 'Admin User',
      timestamp: new Date('2024-11-28T11:20:00'),
    },
  ];
};

export function BonusAuditProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<BonusAuditEntry[]>(() => {
    const saved = localStorage.getItem('bonus_audit_entries');
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.map((e: BonusAuditEntry) => ({
        ...e,
        timestamp: new Date(e.timestamp),
      }));
    }
    return generateMockAuditEntries();
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem('bonus_audit_entries', JSON.stringify(entries));
  }, [entries]);

  const addEntry = useCallback((
    agentId: string,
    agentName: string,
    actionType: BonusAuditActionType,
    amount: number,
    reason: string,
    performedBy: string,
    performedByName: string
  ) => {
    const newEntry: BonusAuditEntry = {
      id: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      agentId,
      agentName,
      actionType,
      amount,
      reason,
      performedBy,
      performedByName,
      timestamp: new Date(),
    };

    setEntries(prev => [newEntry, ...prev]);
  }, []);

  const getEntriesForAgent = useCallback((agentId: string) => {
    return entries
      .filter(e => e.agentId === agentId)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }, [entries]);

  const getTotalAdjustmentsForAgent = useCallback((agentId: string) => {
    return entries
      .filter(e => e.agentId === agentId)
      .reduce((sum, e) => sum + (e.actionType === 'ADD' ? e.amount : -e.amount), 0);
  }, [entries]);

  return (
    <BonusAuditContext.Provider value={{
      entries,
      addEntry,
      getEntriesForAgent,
      getTotalAdjustmentsForAgent,
    }}>
      {children}
    </BonusAuditContext.Provider>
  );
}

export function useBonusAudit() {
  const context = useContext(BonusAuditContext);
  if (context === undefined) {
    throw new Error('useBonusAudit must be used within a BonusAuditProvider');
  }
  return context;
}
